import type { ReactElement } from "react";
import { useMemo } from "react";

import type { ChipItem } from "app/pages/sales-intelligence/common-components/filters-panel";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { LimitedChipItems } from "app/pages/sales-intelligence/common-components/filters-panel";
import { FilterResultContainer } from "app/pages/sales-intelligence/common-components/filters-panel/components/styles";
import { FilterChipVariant } from "app/pages/sales-intelligence/common-components/filters-panel/types/commonEnums";
import { MAX_CHIPS_GROUP_AMOUNT } from "app/pages/sales-intelligence/modules/leads-search-filters/constants";
import type FindTopCountryFilter from "app/pages/sales-intelligence/modules/leads-search-filters/models/TopCountryFilter/TopCountryFilter";
import CountryService from "app/services/CountryService";

const TopCountryFilterResult = ({
    value,
    isExpanded,
    isActionEnabled = true,
    onGetLifecycleStatus,
    onFilterUpdate,
    onFilterValueRemove,
}: IFilterViewBaseProps<FindTopCountryFilter>): ReactElement | null => {
    const handleRemove = (id: string) => {
        const numericId = Number(id);
        if (isNaN(numericId)) {
            return;
        }

        const newValue = value?.filter((v) => v !== numericId);

        onFilterValueRemove?.(newValue);
        onFilterUpdate(newValue);
    };

    const chipItems: ChipItem[] = useMemo(() => {
        if (!value?.length) {
            return [];
        }
        return value.map((code): ChipItem => {
            const country = CountryService.getCountryById(code);
            return {
                id: String(country.id),
                variant: FilterChipVariant.Country,
                text: country.text,
                chipStatus: onGetLifecycleStatus?.(code),
            };
        });
    }, [value, onGetLifecycleStatus]);

    if (!chipItems.length) {
        return null;
    }

    return (
        <FilterResultContainer>
            <LimitedChipItems
                items={chipItems}
                onDelete={handleRemove}
                maxItems={MAX_CHIPS_GROUP_AMOUNT}
                hideActionButton={!isActionEnabled}
                isTabExpanded={isExpanded}
            />
        </FilterResultContainer>
    );
};

export default TopCountryFilterResult;
